import { useCallback } from "react";
import { Observable, RequestHandler } from "@apollo/client";

import { ApiError } from "api/api-client";
import { queryGqlSchema } from "api/graphql-client";
import { useAppVersion } from "contexts/AppVersion/AppVersionContext";

import { RequestContextOptions } from "./request-context-utils";

export const useUnauthSchemaHttpLink = (
  schemaName:
    | "UNAUTHENTICATED_COPILOT_API_DEVELOPER"
    | "UNAUTHENTICATED_ACCOUNT",
): RequestHandler => {
  const { updateMinimumCodeVersionIfNeeded } = useAppVersion();

  return useCallback(
    (operation) =>
      new Observable((observer) => {
        let requestContext;
        try {
          requestContext = (
            operation.getContext() as RequestContextOptions<typeof schemaName>
          ).requestContext!;
        } catch (e) {
          observer.error(e);
          return;
        }

        const { promise, cancel } = queryGqlSchema<typeof schemaName>(
          schemaName,
          operation,
          requestContext,
          schemaName,
        );

        promise
          .then(({ data, requestId, minimumCodeVersion }) => {
            if (minimumCodeVersion) {
              updateMinimumCodeVersionIfNeeded(minimumCodeVersion);
            }
            operation.setContext({ requestId });
            observer.next(data);
            observer.complete();
          })
          .catch((error: unknown) => {
            if (error instanceof ApiError) {
              if (error.isCanceled) return; // Don't propagate cancellation errors.
              operation.setContext({ requestId: error.requestId });
            }
            observer.error(error);
          });

        return cancel;
      }),
    [schemaName, updateMinimumCodeVersionIfNeeded],
  );
};
